// frontend/src/components/ProjectFilesAndComments.js
// CORREGIDO: Añadida lógica para mostrar condicionalmente el slot de "RV Tools".

import React, { useState, useMemo, useRef } from 'react';
import { useProjectStore } from '../store/projectStore';
import { useUIStore } from '../store/uiStore';
import { 
    DocumentTextIcon, 
    ChatBubbleLeftEllipsisIcon, 
    ArrowUpTrayIcon, 
    TrashIcon, 
    EyeIcon, 
    DocumentCheckIcon,
    FolderIcon
} from '@heroicons/react/24/solid';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function ProjectFilesAndComments() {
  const { selectedProject, addProjectComment, uploadProjectFile, deleteProjectFile } = useProjectStore();
  const { isLoading } = useUIStore();
  
  const [activeTab, setActiveTab] = useState('documents');
  const fileInputRefs = useRef({});

  const isMapProject = selectedProject?.initiativeType?.name === 'MAP';

  // --- LÓGICA CORREGIDA Y MEJORADA ---
  // Agrupa y filtra los documentos requeridos.
  const groupedDocumentSlots = useMemo(() => {
    if (!selectedProject?.initiativeType?.documentSlots) return {};
    
    // 1. Filtrado condicional ANTES de agrupar
    const filteredSlots = selectedProject.initiativeType.documentSlots.filter(slot => {
      // Si el slot es "RV Tools", solo se muestra si la herramienta de assessment es la correcta.
      if (slot.key === 'rvTools') {
        return selectedProject.assessmentTool === 'RV tools';
      }
      // Todos los demás slots se muestran siempre.
      return true;
    });

    // 2. Agrupar los slots filtrados por fase
    const groups = {};
    filteredSlots.forEach(slot => {
      const phaseName = slot.templatePhase?.name || 'General';
      if (!groups[phaseName]) {
        groups[phaseName] = [];
      }
      groups[phaseName].push(slot);
    });

    // 3. Ordenar las fases para una mejor UX
    const orderedGroups = {};
    const phaseOrder = ['OLA', 'MRA', 'MPA', 'General']; 
    phaseOrder.forEach(phaseName => {
        if(groups[phaseName]) {
            orderedGroups[phaseName] = groups[phaseName];
        }
    });
    Object.keys(groups).forEach(phaseName => {
        if(!orderedGroups[phaseName]) {
            orderedGroups[phaseName] = groups[phaseName];
        }
    });

    return orderedGroups;
  }, [selectedProject]);

  const [activeDocTab, setActiveDocTab] = useState(Object.keys(groupedDocumentSlots)[0] || 'Comentarios');

  const handleFileChange = async (e, slot) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', slot.key);
      formData.append('phaseName', slot.templatePhase?.name || 'General');
      
      await uploadProjectFile(selectedProject.id, formData);
      
      if (fileInputRefs.current[slot.key]) {
        fileInputRefs.current[slot.key].value = "";
      }
    }
  };

  const triggerFileInput = (slotKey) => {
    fileInputRefs.current[slotKey]?.click();
  };

  const getFileForDocumentSlot = (slotKey) => {
    return (selectedProject?.files || []).find(file => file.documentType === slotKey);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };
  
  const [newComment, setNewComment] = useState('');
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      addProjectComment(selectedProject.id, newComment);
      setNewComment('');
    }
  };

  const renderDocumentList = (slots) => {
    if (!slots || slots.length === 0) {
        return (
            <div className="text-center py-10 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Sin Documentos Requeridos</h3>
                <p className="mt-1 text-sm text-gray-500">Este tipo de iniciativa no tiene documentos predefinidos para adjuntar.</p>
            </div>
        );
    }

    return (
      <ul className="space-y-3">
        {slots.map((slot) => {
          const uploadedFile = getFileForDocumentSlot(slot.key);
          return (
            <li key={slot.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
              <div className="flex items-center">
                <DocumentTextIcon className="h-6 w-6 mr-3 text-gray-400" />
                <span className="font-medium text-gray-800">{slot.name}</span>
              </div>
              <div className="flex items-center space-x-2">
                {uploadedFile ? (
                  <>
                    <a href={`${API_BASE_URL}${uploadedFile.filePath}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-green-700 font-semibold truncate max-w-xs" title={uploadedFile.originalName}>
                      <DocumentCheckIcon className="h-5 w-5 mr-2 text-green-500"/>
                      {uploadedFile.originalName}
                    </a>
                    <a href={`${API_BASE_URL}${uploadedFile.filePath}`} target="_blank" rel="noopener noreferrer" className="p-2 text-blue-600 hover:bg-blue-100 rounded-full" title="Ver archivo">
                      <EyeIcon className="h-5 w-5" />
                    </a>
                    <button onClick={() => deleteProjectFile(uploadedFile.id)} disabled={isLoading} className="p-2 text-red-500 hover:bg-red-100 rounded-full" title="Eliminar archivo">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="file"
                      ref={el => fileInputRefs.current[slot.key] = el}
                      className="hidden"
                      onChange={(e) => handleFileChange(e, slot)}
                    />
                    <button onClick={() => triggerFileInput(slot.key)} disabled={isLoading} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 flex items-center space-x-2">
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      <span>Subir Archivo</span>
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderTabs = () => {
    const documentTabs = Object.keys(groupedDocumentSlots);
    const tabs = isMapProject ? [...documentTabs, 'Comentarios'] : ['Documentos', 'Comentarios'];
    
    return (
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          {tabs.map((tabName) => (
            <button
              key={tabName}
              onClick={() => isMapProject ? setActiveDocTab(tabName) : setActiveTab(tabName.toLowerCase())}
              className={`${
                (isMapProject ? activeDocTab === tabName : activeTab === tabName.toLowerCase())
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center capitalize`}
            >
              {tabName === 'Comentarios' ? <ChatBubbleLeftEllipsisIcon className="h-5 w-5 mr-2"/> : <DocumentTextIcon className="h-5 w-5 mr-2"/>}
              {tabName}
            </button>
          ))}
        </nav>
      </div>
    );
  };

  const renderContent = () => {
    if (isMapProject) {
        if (activeDocTab === 'Comentarios') {
            return renderCommentsSection();
        }
        return renderDocumentList(groupedDocumentSlots[activeDocTab]);
    }

    if (activeTab === 'documents') {
        const allSlots = Object.values(groupedDocumentSlots).flat();
        return renderDocumentList(allSlots);
    }
    return renderCommentsSection();
  };

  const renderCommentsSection = () => (
    <div className="space-y-4">
      <div className="h-64 overflow-y-auto bg-white p-3 rounded-lg border space-y-3">
        {selectedProject?.comments?.length > 0 ? (
          selectedProject.comments.map(comment => (
            <div key={comment.id} className="p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-800">{comment.text}</p>
              <p className="text-xs text-gray-500 text-right mt-1">
                - {comment.user?.username || 'desconocido'} el {formatDate(comment.createdAt)}
              </p>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 pt-16">No hay comentarios aún.</p>
        )}
      </div>
      <form onSubmit={handleCommentSubmit}>
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Añadir un comentario..."
          className="w-full p-2 border rounded-md"
          rows="3"
        />
        <button type="submit" disabled={!newComment.trim() || isLoading} className="mt-2 w-full px-4 py-2 bg-indigo-600 text-white rounded-md shadow-sm hover:bg-indigo-700 disabled:bg-indigo-300">
          Agregar Comentario
        </button>
      </form>
    </div>
  );

  return (
    <div className="mt-10 pt-6 border-t">
      <div className="mb-6">
        {renderTabs()}
      </div>
      <div>
        {renderContent()}
      </div>
    </div>
  );
}

export default ProjectFilesAndComments;
